import { keepPreviousData, queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { AssignSystemToCustomerRequest, AssignSystemToCustomerResponse, GetCustomerSystemsRequest, GetCustomerSystemsResponse, GetIngestionHistoryResponse, ListCustomersRequest, ListCustomersResponse, ListUnassignedSystemsQuery, ListUnassignedSystemsResponse, SystemCustomerResponse, TriggerIngestionRequest, TriggerIngestionResponse } from '../domain/customer.types';
import { customerService } from './customerService';
import { notifications } from '@mantine/notifications';

export const customerKeys = {
  all: ['customers'] as const,
  lists: () => [...customerKeys.all, 'list'] as const,
  list: (filters: ListCustomersRequest) => [...customerKeys.lists(), filters] as const,
  details: () => [...customerKeys.all, 'detail'] as const,
  detail: (customerId: string) => [...customerKeys.details(), customerId] as const,
  // Any single `{customerId}` specific queries should be under the detail key hierarchy
  detailSubQueries: {
    base: (customerId: string) => customerKeys.detail(customerId),
    systems: (customerId: string, filters: Omit<GetCustomerSystemsRequest, 'customerId'>) => [...customerKeys.detail(customerId), 'systems', filters] as const,
  },
  systemCustomer: (systemId: string) => [...customerKeys.all, 'system-customer', systemId] as const,
  ingestionHistory: () => [...customerKeys.all, 'ingestion-history'] as const,
  spotlightSearch: (filters: ListCustomersRequest) => [...customerKeys.all, 'spotlight-search', filters] as const,
  unassignedSystems: (filters: ListUnassignedSystemsQuery) => [...customerKeys.all, 'unassigned-systems', filters] as const,
};

export function useListCustomersQuery(request: ListCustomersRequest = {}) {
  return useQuery<ListCustomersResponse, AxiosError>({
    queryKey: customerKeys.list(request),
    queryFn: () => customerService.listCustomers(request),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCustomerSystemsQuery(request: GetCustomerSystemsRequest) {
  const {customerId, ...filters} = request;

  // Provide default values for pagination if not specified
  const requestWithDefaults: GetCustomerSystemsRequest = {
    ...request,
    page: request.page ?? 1,
    pageSize: request.pageSize ?? 50,
  };

  return useQuery<GetCustomerSystemsResponse, AxiosError>({
    queryKey: customerKeys.detailSubQueries.systems(customerId, filters),
    queryFn: () => customerService.getCustomerSystems(requestWithDefaults),
    staleTime: 5 * 60 * 1000,
    enabled: !!customerId,
  });
}

export function useSystemCustomerQuery(systemId: string) {
  return useQuery<SystemCustomerResponse, AxiosError>({
    queryKey: customerKeys.systemCustomer(systemId),
    queryFn: () => customerService.getSystemCustomer(systemId),
    staleTime: 5 * 60 * 1000,
    enabled: !!systemId,
  });
}

export function useIngestionHistoryQuery(take: number = 10) {
  return useQuery<GetIngestionHistoryResponse, AxiosError>({
    queryKey: [...customerKeys.ingestionHistory(), take],
    queryFn: () => customerService.getIngestionHistory(take),
  });
}

export function useTriggerIngestionMutation() {
  const queryClient = useQueryClient();

  return useMutation<TriggerIngestionResponse, AxiosError, TriggerIngestionRequest>({
    mutationFn: customerService.triggerExternalDataIngestion,
    onSuccess: () => {
      // Invalidate all ingestion history queries regardless of 'take' parameter
      queryClient.invalidateQueries({
        queryKey: customerKeys.ingestionHistory(),
        exact: false // This ensures all queries starting with this key are invalidated
      });
      // Invalidate customer lists to potentially show updated data
      queryClient.invalidateQueries({queryKey: customerKeys.lists()});
    },
  });
}

export function useCustomerListForSpotlightSearch(request: ListCustomersRequest, isEnabled: boolean) {
  return useQuery<ListCustomersResponse, AxiosError>({
    queryKey: customerKeys.spotlightSearch(request),
    queryFn: () => customerService.listCustomers(request),
    placeholderData: keepPreviousData,
    enabled: isEnabled,
  });
}

export function useUnassignedSystemsQuery(request: ListUnassignedSystemsQuery = {}) {
  const requestWithDefaults: ListUnassignedSystemsQuery = {
    page: request.page ?? 1,
    pageSize: request.pageSize ?? 20
  };

  return useQuery<ListUnassignedSystemsResponse, AxiosError>({
    queryKey: customerKeys.unassignedSystems(requestWithDefaults),
    queryFn: () => customerService.getUnassignedSystems(requestWithDefaults),
    placeholderData: keepPreviousData, // Prevent layout shift during pagination
  });
}

export function useAssignSystemToCustomerMutation(options?: {
  onSuccess?: (responses: Array<AssignSystemToCustomerResponse>, variables: AssignSystemToCustomerRequest) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation<Array<AssignSystemToCustomerResponse>, AxiosError, AssignSystemToCustomerRequest>({
    mutationFn: customerService.assignSystemToCustomer,
    onSuccess: (responses, variables) => {
      const successful = responses.filter(r => r.success);
      const failed = responses.filter(r => !r.success);

      // Show notifications
      if (successful.length > 0) {
        notifications.show({
          title: 'Assignment Successful',
          message: `${successful.length} system(s) assigned successfully`,
          color: 'green',
        });
      }

      if (failed.length > 0) {
        const failureReasons = failed.map(r => `${r.systemId}: ${r.failureReason}`).join('; ');
        notifications.show({
          title: 'Assignment Failed',
          message: `${failed.length} system(s) failed to assign. Reasons: ${failureReasons}`,
          color: 'red',
          autoClose: 10000,
        });
      }

      // Invalidate affected queries
      const customerIds = [...new Set(responses.map(r => r.customerId).filter(Boolean))];
      customerIds.forEach(customerId => {
        queryClient.invalidateQueries({
          queryKey: customerKeys.detail(customerId),
          exact: false
        });
      });

      // Invalidate systemCustomer queries for all assigned systems
      successful.forEach(response => {
        queryClient.invalidateQueries({
          queryKey: customerKeys.systemCustomer(response.systemId),
        });
      });

      // Invalidate unassigned systems
      queryClient.invalidateQueries({
        queryKey: customerKeys.unassignedSystems({}),
        exact: false
      });

      // Call custom onSuccess if provided
      options?.onSuccess?.(responses, variables);
    },
    onError: () => {
      notifications.show({
        title: 'Assignment Error',
        message: 'An error occurred while assigning systems. Please try again.',
        color: 'red',
      });
    }
  });
}

export function useToggleFavoriteCustomerMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: customerService.toggleFavoriteCustomer,
    onMutate: async (customerId) => {
      await queryClient.cancelQueries({queryKey: customerKeys.lists()});

      const previousCustomers = queryClient.getQueriesData<ListCustomersResponse>({
        queryKey: customerKeys.lists()
      });

      queryClient.setQueriesData<ListCustomersResponse>(
        {queryKey: customerKeys.lists()},
        (old) => {
          if (!old) return old;
          return {
            ...old,
            customers: old.customers.map((customer) =>
              customer.customerId === customerId
                ? {...customer, isFavorite: !customer.isFavorite}
                : customer
            ),
          };
        }
      );

      return {previousCustomers};
    },
    onError: (_err, _customerId, context) => {
      context?.previousCustomers.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });

      notifications.show({
        title: 'Failed to Update Favorite',
        message: 'Could not update customer favorite status. Please try again.',
        color: 'red',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: customerKeys.lists()});
    },
  });
}

// Query Options
export const customerQueryOptions = (customerId: string) => queryOptions({
  queryKey: customerKeys.detail(customerId),
  queryFn: () => customerService.getCustomerById(customerId),
  staleTime: 5 * 60 * 1000, // 5 minutes
});